import React, { useRef, useState } from "react";
import { useAppContext } from "../context/AppContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import IconTick from "../../../assets/icon-google-tick.svg";
import rehypeHighlight from "rehype-highlight";

const ChatSystemMessage = () => {
  const markdownRef = useRef<HTMLDivElement>(null);
  const { conversation, mode } = useAppContext();
  const [showCopyBadge, setShowCopyBadge] = useState(false);
  const { isChatLoading, isMobile } = useAppContext();

  const stripAllFontFormatting = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Remove all style attributes from all elements
    doc.querySelectorAll("*").forEach((el) => {
      el.removeAttribute("style");
      el.removeAttribute("face");
      el.removeAttribute("size");
      el.removeAttribute("color");
      el.removeAttribute("class");
    });

    // Unwrap span, font tags while keeping content
    ["font", "span"].forEach((tag) => {
      doc.querySelectorAll(tag).forEach((el) => {
        const parent = el.parentNode;
        if (parent) {
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
        }
      });
    });

    return doc.body.innerHTML;
  };

  const handleInsert = async () => {
    const dummyText = markdownRef.current.innerHTML;
    let subject = "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(dummyText, "text/html");

    const subjectNode = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5, h6")).find((heading) =>
      heading.textContent.trim().toLowerCase().startsWith("subject:")
    );

    if (subjectNode) {
      subject = subjectNode.textContent.replace(/^Subject:\s*/i, "").trim();
      subjectNode.remove();
      console.log("Subject extracted:", subject);
    } else {
      console.log("No Subject: heading found");
    }
    let updatedResponse = doc.body.innerHTML;
    updatedResponse = `<div style="all: inherit; font: inherit;">${updatedResponse}</div>`;
    updatedResponse = stripAllFontFormatting(updatedResponse);

    const item = Office.context.mailbox.item;

    // Check if we're already in compose mode
    const isComposeMode =
      item.itemType === Office.MailboxEnums.ItemType.Message && item.body.setSelectedDataAsync;

    if (mode === "reply") {
      if (isComposeMode) {
        // Already in reply/compose mode - insert directly
        item.body.setSelectedDataAsync(
          updatedResponse,
          { coercionType: Office.CoercionType.Html },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log("✓ Response inserted in reply");
            } else {
              console.error("✗ Failed to insert:", result.error);
            }
          }
        );
      } else {
        // In read mode - open reply window
        item.displayReplyAllForm({ htmlBody: updatedResponse });
      }
    } else if (mode === "compose") {
      // Set subject if extracted
      if (subject) {
        item.subject.setAsync(subject, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("✓ Subject set:", subject);
          } else {
            console.error("✗ Failed to set subject:", result.error);
          }
        });
      }

      if (isComposeMode) {
        // Already composing - insert directly
        item.body.setSelectedDataAsync(
          updatedResponse,
          { coercionType: Office.CoercionType.Html },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log("✓ Response inserted in compose");
            } else {
              console.error("✗ Failed to insert:", result.error);
            }
          }
        );
      } else {
        // Open new compose window
        Office.context.mailbox.displayNewMessageForm({
          toRecipients: [],
          subject: subject,
          htmlBody: updatedResponse,
        });
        console.log("✓ Compose window opened");
      }
    }
  };

  const handleCopyMessage = () => {
    const htmlContent =
      markdownRef.current?.innerHTML || conversation[conversation.length - 1]?.body;

    const tempElement = document.createElement("div");
    tempElement.innerHTML = htmlContent;
    document.body.appendChild(tempElement);

    const range = document.createRange();
    range.selectNode(tempElement);

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
    selection.removeAllRanges();

    document.body.removeChild(tempElement);

    setShowCopyBadge(true);
    setTimeout(() => {
      setShowCopyBadge(false);
    }, 1000);
  };

  return (
    <>
      <div className="system-msg">
        <div className="title">
          <img src="/assets/Elysia-logo.svg" alt="logo" />
        </div>
        <div ref={markdownRef}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeHighlight]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" className="markdown-link" />
              ),
              table: ({ node, ...props }) => (
                <div className="markdown-table-container">
                  <table className="markdown-table" {...props} />
                </div>
              ),
              code: ({ node, inline, className, children, ...props }: any) => {
                const match = /language-(\w+)/.exec(className || "");
                const language = match ? match[1] : "";

                return !inline ? (
                  <div className="code-block-wrapper">
                    <div className="code-block-header">
                      <span className="code-language">{language || "code"}</span>
                    </div>
                    <pre className={`code-block language-${language}`}>
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  </div>
                ) : (
                  <code className="inline-code" {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {conversation[conversation.length - 1]?.body}
          </ReactMarkdown>
        </div>

        {!isChatLoading && (
          <div className="msg-footer">
            <button
              className={`insert-btn ${isMobile ? "insert-mobile" : ""}`}
              onClick={() => handleInsert()}
            >
              Insert Mail
            </button>

            <button className="insert-btn copy-btn" onClick={handleCopyMessage}>
              <img src="/assets/icon-copy.png" alt="" />
            </button>
            {showCopyBadge && (
              <span className="badge-for-copy-button">
                <IconTick />
                <span>Copied</span>
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ChatSystemMessage;
